import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Phone, Clock, TrendingUp, Users, CheckCircle, AlertCircle, 
  PhoneOff, DollarSign, ArrowRight, Shield, Zap, Sparkles, 
  Calendar, MessageSquare, XCircle, UserX, UserCheck, Activity 
} from 'lucide-react';

const AIReceptionistExperienceBlog = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.07);
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-white"></div>
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-sm font-medium border border-blue-100 mb-8">
            <Sparkles className="w-4 h-4" />
            <span>Patient Experience Insights</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-8 text-gray-900 leading-tight tracking-tight">
            The Right Way to Think About <br/>
            <span className="text-blue-600">Patient Experience</span> with AI
          </h1>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed mb-8">
            Why comparing AI to human staff misses the point—and how the real value lies in capturing the patients you're currently losing to voicemail.
          </p>

          <div className="flex items-center justify-center space-x-4 text-sm text-gray-500 font-medium">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">A</div>
              <span>Asha Team</span>
            </div>
            <span>•</span>
            <span>Dec 20, 2024</span>
            <span>•</span>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>8 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="pb-24 px-6">
        <div className="container mx-auto max-w-4xl">
          
          {/* Introduction */}
          <div className="prose prose-lg prose-blue max-w-none mb-16">
            <p className="text-xl text-gray-600 leading-relaxed font-medium">
              When dental practices first consider implementing an AI receptionist, the most common question we hear is: 
              <span className="text-gray-900 font-bold bg-blue-50 px-2 py-0.5 rounded mx-1">"Will my patients be okay talking to AI?"</span>
            </p>
            <p className="text-gray-600 leading-relaxed">
              It's a fair concern. Healthcare is personal. Patients expect empathy. But this question reveals a fundamental misunderstanding about how AI receptionists actually work in modern dental practices—and where their true value lies.
            </p>
          </div>

          {/* The Visual Insight */}
          <div className="my-16 relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 transform -skew-y-2 rounded-3xl -z-10"></div>
            <div className="bg-white rounded-[2rem] p-8 md:p-12 shadow-xl border border-gray-100">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">The Wrong Comparison</h2>
                  <p className="text-gray-600 mb-6">
                    Most dentists compare AI to their best receptionist on their best day.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 opacity-50">
                      <Users className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-500 font-medium decoration-line-through">AI vs. Human Receptionist</span>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute -left-6 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-lg z-10 hidden md:block">
                    <ArrowRight className="w-5 h-5 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-blue-600 mb-4">The Real Comparison</h2>
                  <p className="text-gray-600 mb-6">
                    You should be comparing AI to the alternative: <span className="font-semibold text-gray-900">No one answering at all.</span>
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-blue-50 border border-blue-100 shadow-sm">
                      <Zap className="w-5 h-5 text-blue-600" />
                      <span className="text-gray-900 font-bold">AI vs. Voicemail</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="prose prose-lg prose-blue max-w-none mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">The "Ghost Patient" Problem</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Here is the hard truth about patient behavior in 2024: <strong className="text-gray-900">Patients do not leave voicemails.</strong>
            </p>
            <p className="text-gray-600 leading-relaxed">
              When a potential new patient calls your office and gets sent to voicemail, they don't patiently wait for you to call back. They hang up, go back to Google, and call the next dentist on the list. We call these "Ghost Patients"—revenue that vanishes before you even know it existed.
            </p>
          </div>

          {/* Visual Comparison: The Tale of Two Calls */}
          <div className="my-20">
            <h3 className="text-2xl font-bold text-center mb-10">The Tale of Two After-Hours Calls</h3>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Scenario A: Traditional */}
              <div className="bg-white rounded-3xl p-8 border border-red-100 shadow-lg relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-400 to-red-600"></div>
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                      <PhoneOff className="w-5 h-5 text-red-500" />
                    </div>
                    <span className="font-bold text-gray-900">Without AI</span>
                  </div>
                  <span className="text-xs font-bold text-red-500 bg-red-50 px-2 py-1 rounded-full">LOST REVENUE</span>
                </div>
                
                <div className="space-y-6 relative z-10">
                  <div className="flex gap-4">
                    <div className="flex-col items-center flex">
                      <div className="w-3 h-3 rounded-full bg-red-200"></div>
                      <div className="w-0.5 h-full bg-red-100 my-1"></div>
                    </div>
                    <div className="pb-4">
                      <p className="text-sm font-bold text-gray-900">5:15 PM</p>
                      <p className="text-sm text-gray-600">Patient calls with tooth pain.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-col items-center flex">
                      <div className="w-3 h-3 rounded-full bg-red-300"></div>
                      <div className="w-0.5 h-full bg-red-100 my-1"></div>
                    </div>
                    <div className="pb-4">
                      <p className="text-sm font-bold text-gray-900">5:16 PM</p>
                      <p className="text-sm text-gray-600">Goes to voicemail. Patient hangs up.</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-col items-center flex">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">5:17 PM</p>
                      <p className="text-sm text-gray-600">Calls competitor. <span className="font-semibold text-red-600">Booked elsewhere.</span></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Scenario B: With Amy */}
              <div className="bg-white rounded-3xl p-8 border border-blue-100 shadow-xl relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-indigo-600"></div>
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="font-bold text-gray-900">With Amy</span>
                  </div>
                  <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded-full">+$1,500 VALUE</span>
                </div>
                
                <div className="space-y-6 relative z-10">
                  <div className="flex gap-4">
                    <div className="flex-col items-center flex">
                      <div className="w-3 h-3 rounded-full bg-blue-200"></div>
                      <div className="w-0.5 h-full bg-blue-100 my-1"></div>
                    </div>
                    <div className="pb-4">
                      <p className="text-sm font-bold text-gray-900">5:15 PM</p>
                      <p className="text-sm text-gray-600">Patient calls with tooth pain.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-col items-center flex">
                      <div className="w-3 h-3 rounded-full bg-blue-400"></div>
                      <div className="w-0.5 h-full bg-blue-100 my-1"></div>
                    </div>
                    <div className="pb-4">
                      <p className="text-sm font-bold text-gray-900">5:15 PM</p>
                      <p className="text-sm text-gray-600">Amy answers instantly. Empathizes.</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-col items-center flex">
                      <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">5:17 PM</p>
                      <p className="text-sm text-gray-600">Checks schedule. <span className="font-semibold text-green-600">Appointment booked for 9AM.</span></p>
                    </div>
                  </div>
                </div>

                {/* Glass card overlay decoration */}
                <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-blue-400/10 rounded-full blur-2xl"></div>
              </div>
            </div>
          </div>

          <div className="prose prose-lg prose-blue max-w-none mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">When Does Amy Actually Answer?</h2>
            <p className="text-gray-600 leading-relaxed">
              We recommend setting up Amy in <strong>"Overflow & After-Hours"</strong> mode. This means your friendly front desk staff still answers every call they can. Amy only steps in when:
            </p>
          </div>

          {/* Use Cases Grid - Similar to Bento Grid in Reference */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm mb-4 text-purple-600">
                <Clock className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">After Hours</h3>
              <p className="text-sm text-gray-600">
                Evenings and weekends when your practice is closed but patients are still looking for help.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm mb-4 text-blue-600">
                <Activity className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Busy Overflow</h3>
              <p className="text-sm text-gray-600">
                When all your lines are tied up or your staff is checking out a patient in the office.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm mb-4 text-green-600">
                <Users className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Staff Absence</h3>
              <p className="text-sm text-gray-600">
                Lunch breaks, sick days, or unexpected staff shortages. Amy ensures coverage 24/7.
              </p>
            </div>
          </div>

          {/* The ROI Section */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-[2.5rem] p-8 md:p-12 text-white relative overflow-hidden mb-20 shadow-2xl">
            {/* Background Effects */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>

            <div className="relative z-10 grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-blue-300 text-xs font-bold border border-white/10 mb-6">
                  <TrendingUp className="w-3 h-3" />
                  THE REAL NUMBERS
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  The Revenue Impact
                </h2>
                <p className="text-slate-300 text-lg leading-relaxed mb-8">
                  For a single-doctor practice, missing just 6 new patient calls a month is a significant loss. Here is what capturing those calls looks like for your bottom line.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400">
                      <CheckCircle className="w-4 h-4" />
                    </div>
                    <span className="text-slate-200">6-7 new patients rescued / month</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400">
                      <CheckCircle className="w-4 h-4" />
                    </div>
                    <span className="text-slate-200">$1,500 avg. first-year value</span>
                  </div>
                </div>
              </div>

              {/* Calculator Visual */}
              <div className="glass-card bg-white/10 rounded-2xl p-8 border border-white/10 backdrop-blur-md">
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between text-sm text-slate-400 mb-1">
                      <span>Missed Calls Rescued</span>
                      <span className="text-white font-mono">6</span>
                    </div>
                    <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
                      <div className="h-full w-1/4 bg-blue-500 rounded-full"></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm text-slate-400 mb-1">
                      <span>Patient Value</span>
                      <span className="text-white font-mono">$1,500</span>
                    </div>
                    <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
                      <div className="h-full w-3/4 bg-purple-500 rounded-full"></div>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-white/10">
                    <p className="text-sm text-slate-400 mb-2">Annual Revenue Added</p>
                    <p className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
                      $108,000
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="prose prose-lg prose-blue max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">The Bottom Line</h2>
            <p className="text-gray-600 leading-relaxed mb-8">
              The choice isn't between "AI Receptionist" and "Human Receptionist." The choice is between "Always Answering" and "Sometimes Answering."
            </p>
            <p className="text-gray-600 leading-relaxed">
              When you frame it that way, the decision becomes easy. Your patients deserve to be heard, whether it's 2 PM on a Tuesday or 8 PM on a Sunday. With Amy, they always will be.
            </p>
          </div>

        </div>
      </article>

      {/* Footer CTA */}
      <section className="py-20 px-6 bg-gray-50 border-t border-gray-200">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to stop losing patients to voicemail?
          </h2>
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
            See exactly how Amy handles calls, schedules appointments, and runs on Asha PMS after a guided migration.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-blue-600 text-white hover:bg-blue-700 px-8 py-6 rounded-xl text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-300">
              Book a Demo
            </Button>
            <Button variant="outline" className="px-8 py-6 rounded-xl text-lg font-bold bg-white hover:bg-gray-50">
              Listen to Calls
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIReceptionistExperienceBlog;
